// Bulk Archive on the Orders list: which ticked orders can be archived, and
// what to tell the person before and after.

const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

/**
 * Splits the ticked orders into those Archive sends and those it leaves:
 * an order at a location this account can't fulfil from (a warehouse that
 * runs its fulfilment) keeps its status, as the Fulfilment dropdown does.
 * Orders with no location can always be archived. `rows` is the page shown
 * (the table clears ticks when the page changes).
 */
export const archiveSelection = (
  selectedIds,
  rows,
  fulfillableWarehouseIds,
) => {
  const byId = new Map((rows ?? []).map((row) => [row?.id, row]));
  const ids = [];
  const locked = [];
  (selectedIds ?? []).forEach((id) => {
    const warehouseId = byId.get(id)?.warehouse_id;
    if (
      warehouseId != null &&
      !fulfillableWarehouseIds.has(String(warehouseId))
    ) {
      locked.push(id);
    } else {
      ids.push(id);
    }
  });
  return { ids, locked };
};

export const archiveConfirmText = ({ ids, locked }) => {
  const skipped = locked.length
    ? ` ${plural(locked.length, "order")} at a warehouse you can't fulfil from will be left as they are.`
    : "";
  return `Archive ${plural(ids.length, "order")}? They leave the Orders list and nothing else changes: their status, stock and shipments stay as they are. Turn on Archived to see them.${skipped}`;
};

// Which ticked orders are archived (Unarchive brings those back) and which
// aren't (Archive takes those), from the page shown.
export const splitByArchived = (selectedIds, rows) => {
  const byId = new Map((rows ?? []).map((row) => [row?.id, row]));
  const archived = [];
  const notArchived = [];
  (selectedIds ?? []).forEach((id) => {
    if (byId.get(id)?.archived_at) archived.push(id);
    else notArchived.push(id);
  });
  return { archived, notArchived };
};

// The server's answer to Unarchive, as one line for the snackbar.
export const unarchiveResultText = (result) => {
  const unarchived = result?.unarchived_order_ids?.length ?? 0;
  const notArchived = result?.not_archived_order_ids?.length ?? 0;
  const skipped = result?.skipped_order_ids?.length ?? 0;
  const parts = [`${plural(unarchived, "order")} back in the list`];
  if (notArchived) parts.push(`${notArchived} weren't archived`);
  if (skipped) parts.push(`${skipped} skipped`);
  return parts.join(" · ");
};

// The server's answer (archived / already archived / not found), plus the
// orders left out here, as one line for the snackbar.
export const archiveResultText = (result, lockedCount = 0) => {
  const archived = result?.archived_order_ids?.length ?? 0;
  const already = result?.already_archived_order_ids?.length ?? 0;
  const skipped = (result?.skipped_order_ids?.length ?? 0) + lockedCount;
  const parts = [`${plural(archived, "order")} archived`];
  if (already) parts.push(`${already} already archived`);
  if (skipped) parts.push(`${skipped} skipped`);
  return parts.join(" · ");
};
